import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDeal } from '../deals/DealContext';
import { useAuth } from '../auth/AuthContext';
import { isAdmin } from '../auth/roles';
import DeclineChart from './DeclineChart.jsx';
import WellMapV2 from './WellMapV2.jsx';
import { savePool } from '../abs/absLink.js';
import KnowledgeBase, { DataRoom } from './KbDataRoom.jsx';
import { addMonths, eurFrom, forecast, monthsBetween, secantFromNominal, effectiveFromNominal } from './arps.js';
import { typeCurve } from './typecurve.js';
import { fetchReviews, fetchDealAudit, fetchWorkflow, postFreeze, postReview } from './api.js';
import { saveForecastFreezeRef } from './forecastFreezeRef.js';
import { downloadWorkflowDocx, downloadWorkflowXlsx } from './workflowExport.js';
import { sellerCumToDate } from '../utils/commodityDisplay.js';
import './assetWorkflow.css';

export const AW_TABS = [
  { key: 'map', label: 'Map' },
  { key: 'decline', label: 'Decline workbench' },
  { key: 'typecurve', label: 'Type curves' },
  { key: 'review', label: 'Review & approve' },
  { key: 'kb', label: 'Knowledge base' },
  { key: 'dataroom', label: 'Data room' },
];

const mmcf = (v) => (v == null || !Number.isFinite(v) ? '—' : (v / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 }));
const eurUnit = (wf) => (wf?.product === 'oil' ? 'Mbbl' : 'MMcf');
const n1 = (v, d = 1) => (v == null || !Number.isFinite(Number(v)) ? '—' : Number(v).toFixed(d));
const STATUS_PILL = { approved: 'aw-pill-green', reviewed: 'aw-pill-blue', rejected: 'aw-pill-red', draft: 'aw-pill-grey' };

function readTab() {
  const q = (window.location.hash.split('?')[1] || '').split('&').find((s) => s.startsWith('awtab='));
  const t = q ? decodeURIComponent(q.slice(6)) : 'map';
  return AW_TABS.some((x) => x.key === t) ? t : 'map';
}

function Pill({ status, provisional }) {
  if (status === 'approved' && provisional) {
    return <span className="aw-pill aw-pill-provisional" title="Draft approval by Grok Bot for Luther, pending engineer review">approved · DRAFT (bot)</span>;
  }
  return <span className={`aw-pill ${STATUS_PILL[status || 'draft']}`}>{status || 'draft'}</span>;
}

function lastHistMonth(w) {
  return w.history?.length ? w.history[w.history.length - 1][0] : null;
}

function historyCaption(well) {
  if (well?.historyGrain !== 'lease') return {};
  return { historyLabel: 'Lease-level', sourceLabel: well.historySource || null };
}

/** Forecast lines for one well: D2D fit, ARIES case, override preview. */
function wellSeries(w, override) {
  const out = [];
  const end = lastHistMonth(w);
  if (w.fit?.status === 'ok' && w.fit.t0) {
    const n = monthsBetween(w.fit.t0, end) + 1 + 240;
    out.push({ key: 'fit', label: `D2D ${w.fit.model} fit (b=${w.fit.b})`, color: '#e8a317', points: forecast(w.fit, { start: w.fit.t0, months: n, qLimit: w.fit.qLimit }) });
  }
  if (w.aries?.forecast?.length) {
    out.push({ key: 'aries', label: 'Seller forecast case', color: '#2563eb', dash: '6 4', points: w.aries.forecast.map(([date, rate]) => ({ date, rate })) });
  }
  if (override && end) {
    out.push({ key: 'override', label: 'Override (preview)', color: '#8b5cf6', dash: '2 3', points: forecast({ ...override, t0: addMonths(end, 1) }, { start: addMonths(end, 1), months: 240, qLimit: w.fit?.qLimit || 5 }) });
  }
  return out;
}

function WellList({ wells, focusId, onFocus, reviews, selected }) {
  return (
    <div className="aw-well-list" data-testid="well-list">
      {wells.map((w) => (
        <button
          key={w.assetId}
          type="button"
          className={`aw-well-row ${focusId === w.assetId ? 'active' : ''}`}
          onClick={() => onFocus(w.assetId)}
          data-asset={w.assetId}
        >
          <span className="aw-well-name">
            {selected.includes(w.assetId) && <span className="aw-dot" />} {w.name}
          </span>
          <span className={`aw-q aw-q-${w.fit?.quality || 'x'}`}>{w.fit?.quality || '—'}</span>
          <Pill status={reviews[w.assetId]?.status} provisional={reviews[w.assetId]?.provisional} />
        </button>
      ))}
    </div>
  );
}

function Workbench({ wf, well, reviews, audit, canApprove, onReview, busy }) {
  const [ov, setOv] = useState(null);
  const [reason, setReason] = useState('');
  useEffect(() => {
    setOv(null);
    setReason('');
  }, [well?.assetId]);
  if (!well) return <div className="aw-card aw-empty">Select a well on the map or in the list.</div>;
  const f = well.fit || {};
  const vol = eurUnit(wf);
  const sellerCum = sellerCumToDate(wf, well.aries?.cums);
  const rv = reviews[well.assetId];
  const startOv = () =>
    setOv({
      qi: +(f.qNow || f.qi || 100).toFixed(1),
      diEffPct: +(f.declineNowEffPct || 10).toFixed(1),
      b: f.b ?? 0.5,
      dminEffPct: f.dminEffPct ?? 6,
    });
  const ovEur = ov
    ? eurFrom(
        { ...ov, t0: addMonths(lastHistMonth(well), 1), product: well.product || wf?.primaryProduct },
        {
          start: addMonths(lastHistMonth(well), 1),
          qLimit: f.qLimit != null ? f.qLimit : undefined,
          product: well.product || wf?.primaryProduct || (String(wf?.units?.rate || '').toLowerCase().includes('bbl') ? 'oil' : 'gas'),
        },
      )
    : null;
  const series = wellSeries(well, ov);
  const act = (status, basis, extra = {}) => onReview(well.assetId, { status, basis, reason, expectedVersion: rv?.version ?? 0, ...extra });
  return (
    <div className="aw-workbench" data-testid="workbench">
      <div className="aw-card">
        <div className="aw-toolbar">
          <span className="aw-eyebrow">Decline · {well.name}</span>
          <Pill status={rv?.status} provisional={rv?.provisional} />
          <span className="aw-muted aw-mono">API {well.api10 || '—'} · {well.resCat || well.status || ''} · WI {well.wi == null ? '—' : `${n1(well.wi * 100, 1)}%`} · NRI {well.nri == null ? '—' : `${n1(well.nri * 100, 1)}%`}</span>
        </div>
        <DeclineChart history={well.history} series={series} unit={wf.units?.rate} {...historyCaption(well)} />
        {well.historyGrain === 'lease' ? (
          <div className="aw-muted aw-small">History is the lease total, not a single well. D2D fit vs seller forecast uses that lease series.</div>
        ) : null}
      </div>
      <div className="aw-grid-2">
        <div className="aw-card" data-testid="fit-params">
          <div className="aw-eyebrow">D2D fit vs seller forecast{well.historyGrain === 'lease' ? ' · lease-level' : ''}</div>
          <table className="aw-table">
            <thead>
              <tr><th /><th>D2D fit</th><th>Seller</th></tr>
            </thead>
            <tbody>
              <tr><td>Model</td><td>{f.model || f.status}</td><td>{well.aries?.arps?.method || '—'}</td></tr>
              <tr><td>qi ({wf.units?.rate})</td><td className="aw-mono">{n1(f.qi)}</td><td className="aw-mono">{n1(well.aries?.arps?.qi)}</td></tr>
              <tr><td>Di (eff %/yr)</td><td className="aw-mono">{n1(f.diEffPct)}</td><td className="aw-mono">{n1(well.aries?.arps?.diInputPct)}</td></tr>
              <tr><td>b</td><td className="aw-mono">{n1(f.b, 2)}</td><td className="aw-mono">{n1(well.aries?.arps?.b, 2)}</td></tr>
              <tr><td>Terminal (eff %/yr)</td><td className="aw-mono">{n1(f.dminEffPct)}</td><td className="aw-mono">{well.aries?.arps?.dmin ? n1(effectiveFromNominal(well.aries.arps.dmin) * 100) : '—'}</td></tr>
              <tr><td>R² (log) · holdout APE</td><td className="aw-mono">{n1(f.r2, 3)} · {n1(f.holdoutMapePct)}%</td><td /></tr>
              <tr><td>Rate now · decline now</td><td className="aw-mono">{n1(f.qNow)} · {n1(f.declineNowEffPct)}%</td><td /></tr>
              <tr><td>Cum to date ({vol})</td><td className="aw-mono">{mmcf(well.cumToDate)}</td><td className="aw-mono">{sellerCum != null ? n1(sellerCum) : '—'}</td></tr>
              <tr><td>Remaining ({vol})</td><td className="aw-mono">{mmcf(f.remaining)}</td><td className="aw-mono">{mmcf(well.aries?.remaining)}</td></tr>
              <tr><td>EUR ({vol})</td><td className="aw-mono"><b>{mmcf(f.eur)}</b></td><td /></tr>
              <tr><td>Δ remaining vs seller</td><td className="aw-mono" colSpan={2}>{well.compare ? `${n1(well.compare.remainingDeltaPct)}%` : '—'}</td></tr>
            </tbody>
          </table>
          {f.flags?.length ? <div className="aw-flags">{f.flags.map((x) => <span key={x} className="aw-chip aw-chip-amber">{x}</span>)}</div> : null}
          <div className="aw-muted aw-small">Window {f.window?.start} → {f.window?.end} · n={f.n} · grade {f.quality}</div>
          {well.aries?.segments?.length ? <div className="aw-mono aw-small aw-muted">Seller curve: {well.aries.segments.join(' │ ')}</div> : null}
        </div>
        <div className="aw-card" data-testid="review-panel">
          <div className="aw-eyebrow">Review · approve</div>
          {ov ? (
            <div className="aw-override">
              {[
                ['qi', 'qi from today', 1, Math.max(10, (f.qNow || 100) * 3), 0.5],
                ['diEffPct', 'Di eff %/yr', 1, 95, 0.5],
                ['b', 'b', 0, 1.5, 0.05],
                ['dminEffPct', 'Terminal %/yr', 2, 15, 0.5],
              ].map(([k, label, lo, hi, step]) => (
                <label key={k} className="aw-slider">
                  <span>{label}</span>
                  <input type="range" min={lo} max={hi} step={step} value={ov[k]} onChange={(e) => setOv({ ...ov, [k]: Number(e.target.value) })} data-testid={`ov-${k}`} />
                  <span className="aw-mono">{ov[k]}</span>
                </label>
              ))}
              <div className="aw-small">Override remaining: <b className="aw-mono">{mmcf(ovEur)}</b> {vol}</div>
            </div>
          ) : null}
          <textarea className="aw-input" rows={2} placeholder="Reason / reviewer note (required for override or reject)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="review-reason" />
          <div className="aw-actions">
            <button type="button" className="aw-btn" disabled={busy || f.status !== 'ok'} onClick={() => act('reviewed', 'd2dFit')} data-testid="review-fit">Mark reviewed · D2D fit</button>
            <button type="button" className="aw-btn" disabled={busy || !well.aries} onClick={() => act('reviewed', 'aries')} data-testid="review-aries">Mark reviewed · seller forecast</button>
            {ov ? (
              <button type="button" className="aw-btn" disabled={busy} onClick={() => act('reviewed', 'override', { override: ov })} data-testid="review-override">Save override as reviewed</button>
            ) : (
              <button type="button" className="aw-btn" onClick={startOv} data-testid="start-override">Override…</button>
            )}
            <button type="button" className="aw-btn aw-btn-amber" disabled={busy || !canApprove || rv?.status !== 'reviewed'} onClick={() => act('approved', rv?.basis, rv?.basis === 'override' ? { override: rv.params } : {})} data-testid="approve" title={canApprove ? '' : 'Admins approve (four-eyes)'}>Approve</button>
            <button type="button" className="aw-btn aw-btn-red" disabled={busy} onClick={() => act('rejected', rv?.basis || 'd2dFit')} data-testid="reject">Reject</button>
          </div>
          <div className="aw-eyebrow" style={{ marginTop: 12 }}>Audit</div>
          <ul className="aw-audit" data-testid="audit">
            {audit.length === 0 && <li className="aw-muted">No review actions yet.</li>}
            {audit.map((a) => (
              <li key={a.id}>
                <span className="aw-mono">{a.created_at?.slice(0, 16).replace('T', ' ')}Z</span> · {a.user_name} · {a.from_status || '∅'} → <b>{a.to_status}</b> ({a.basis}){a.reason ? ` — ${a.reason}` : ''}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function TypeCurves({ wf, wells, selected }) {
  const vol = eurUnit(wf);
  const [group, setGroup] = useState(selected.length >= 3 ? 'selection' : 'all');
  useEffect(() => {
    if (selected.length >= 3) setGroup('selection');
  }, [selected.length]);
  const server = (wf.typeCurves || []).find((t) => t.group === group);
  const tc = useMemo(() => {
    if (group === 'selection') return { group: 'selection', ...typeCurve(wells.filter((w) => selected.includes(w.assetId))) };
    return server;
  }, [group, server, wells, selected]);
  const pts = (arr) => arr.map((rate, k) => ({ date: addMonths('2000-01', k), rate }));
  const series = tc?.curves?.P50?.length
    ? [
        { key: 'p50', label: 'P50', color: '#163a4f', points: pts(tc.curves.P50), width: 2.5 },
        { key: 'p10', label: 'P10 (high)', color: '#059669', points: pts(tc.curves.P10), dash: '5 3' },
        { key: 'p90', label: 'P90 (low)', color: '#dc2626', points: pts(tc.curves.P90), dash: '5 3' },
      ]
    : [];
  const band = tc?.curves?.P50?.length ? Object.assign(tc.curves.P10.map((hi, k) => ({ date: addMonths('2000-01', k), hi, lo: tc.curves.P90[k] })), { label: 'P10–P90' }) : null;
  return (
    <div className="aw-card" data-testid="type-curves">
      <div className="aw-toolbar">
        <span className="aw-eyebrow">Type curves</span>
        <select value={group} onChange={(e) => setGroup(e.target.value)} data-testid="tc-group">
          {selected.length >= 3 && <option value="selection">Map selection ({selected.length} wells)</option>}
          {(wf.typeCurves || []).map((t) => (
            <option key={t.group} value={t.group}>{t.group} ({t.wellCount})</option>
          ))}
        </select>
        <span className="aw-muted">Time-zero = peak in first 12 producing months · P10 = high (exceedance)</span>
      </div>
      {tc?.months ? (
        <>
          <DeclineChart history={[]} series={series} band={band} unit={wf.units?.rate} monthsAxis xLabel={`${tc.wellCount} wells · ${tc.months} months`} testId="tc-chart" />
          <table className="aw-table">
            <thead><tr><th>Case</th><th>Arps fit</th><th>Type-curve EUR ({vol})</th><th>Well EUR distribution ({vol})</th></tr></thead>
            <tbody>
              {['P10', 'P50', 'P90'].map((k) => (
                <tr key={k}>
                  <td><b>{k}</b></td>
                  <td className="aw-mono">{tc.fits?.[k] ? `${tc.fits[k].model} qi ${n1(tc.fits[k].qi)} Di ${n1(secantFromNominal(tc.fits[k].di, tc.fits[k].b) * 100)}% b ${tc.fits[k].b}` : '—'}</td>
                  <td className="aw-mono">{mmcf(tc.eurTypeCurve?.[k])}</td>
                  <td className="aw-mono">{mmcf(tc.eurWellDistribution?.[k])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <div className="aw-empty">Need ≥3 {wf.summary?.historyGrain === 'lease' ? 'leases' : 'wells'} with ≥6 producing months.</div>
      )}
    </div>
  );
}

function ReviewBoard({ wf, reviews, freeze, onFreeze, busy, onFocus, selected }) {
  const counts = { approved: 0, reviewed: 0, rejected: 0, draft: 0 };
  wf.wells.filter((w) => w.history?.length).forEach((w) => { counts[reviews[w.assetId]?.status || 'draft'] += 1; });
  return (
    <div className="aw-card" data-testid="review-board">
      <div className="aw-toolbar">
        <span className="aw-eyebrow">{wf.summary?.historyGrain === 'lease' ? 'Per-lease forecast review' : 'Per-well forecast review'}</span>
        {Object.entries(counts).map(([k, v]) => (
          <span key={k} className="aw-small"><Pill status={k} /> {v}</span>
        ))}
        <button type="button" className="aw-btn aw-btn-amber" disabled={busy || !counts.approved} onClick={onFreeze} data-testid="freeze">Freeze approved set → scenario</button>
        {freeze && <span className="aw-mono aw-small" data-testid="freeze-hash">sha256 {freeze.contentSha256.slice(0, 16)}… · {freeze.approvedCount} wells · {new Date(freeze.createdAt).toLocaleString()}</span>}
      </div>
      <table className="aw-table aw-table-dense">
        <thead><tr><th>{wf.summary?.historyGrain === 'lease' ? 'Lease' : 'Well'}</th><th>Quality</th><th>D2D remaining</th><th>Seller remaining</th><th>Δ%</th><th>Status</th><th>Basis</th><th>By</th><th>Note</th></tr></thead>
        <tbody>
          {wf.wells.filter((w) => w.history?.length).map((w) => {
            const r = reviews[w.assetId];
            return (
              <tr key={w.assetId} className={selected.includes(w.assetId) ? 'aw-row-sel' : ''} onClick={() => onFocus(w.assetId, 'decline')}>
                <td>{w.name}</td><td>{w.fit?.quality || '—'}</td><td className="aw-mono">{mmcf(w.fit?.remaining)}</td><td className="aw-mono">{mmcf(w.aries?.remaining)}</td>
                <td className="aw-mono">{w.compare ? n1(w.compare.remainingDeltaPct) : '—'}</td><td><Pill status={r?.status} provisional={r?.provisional} /></td><td>{r?.basis || ''}</td><td>{r?.updated_by_name || ''}</td><td className="aw-small">{r?.reason || ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function AssetWorkflow() {
  const { dealId } = useDeal();
  const auth = useAuth();
  const [wf, setWf] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTabState] = useState(readTab);
  const [selected, setSelected] = useState([]);
  const [focusId, setFocusId] = useState(null);
  const [reviews, setReviews] = useState({});
  const [audit, setAudit] = useState([]);
  const [freeze, setFreeze] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const setTab = useCallback((t) => {
    setTabState(t);
    const base = window.location.hash.split('?')[0] || '#assets';
    const q = new URLSearchParams(window.location.hash.split('?')[1] || '');
    q.set('awtab', t);
    window.history.replaceState(null, '', `${base}?${q.toString()}`);
  }, []);

  useEffect(() => {
    let alive = true;
    setWf(null);
    setErr(null);
    fetchWorkflow(dealId)
      .then((d) => {
        if (!alive) return;
        setWf(d);
        setReviews(d.reviews || {});
        setFreeze(d.latestFreeze || null);
        const first = d.wells.find((w) => w.fit?.status === 'ok');
        setFocusId(first?.assetId || null);
      })
      .catch((e) => alive && setErr(e.status === 404 ? 'No asset workflow has been built for this deal yet.' : e.message));
    return () => { alive = false; };
  }, [dealId]);

  useEffect(() => {
    if (!wf || !focusId) return;
    fetchReviews(dealId, focusId).then((d) => setAudit(d.audit || [])).catch(() => setAudit([]));
  }, [dealId, focusId, wf, reviews]);

  const wells = wf?.wells || [];
  const focus = wells.find((w) => w.assetId === focusId) || null;
  const listWells = useMemo(() => {
    const base = wells.filter((w) => w.history?.length);
    return selected.length ? base.filter((w) => selected.includes(w.assetId)) : base;
  }, [wells, selected]);
  const canApprove = isAdmin(auth.user);

  const onReview = async (assetId, body) => {
    setBusy(true);
    try {
      const { review } = await postReview(dealId, assetId, body);
      setReviews((r) => ({ ...r, [assetId]: review }));
      setToast(`${review.status} · ${assetId}`);
    } catch (e) {
      setToast(`Error: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };
  const onFreeze = async () => {
    setBusy(true);
    try {
      const f = await postFreeze(dealId);
      setFreeze(f);
      saveForecastFreezeRef(dealId, f);
      setToast(`Frozen ${f.approvedCount} approved forecasts · ${f.contentSha256.slice(0, 12)}`);
    } catch (e) {
      setToast(`Error: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };
  const focusWell = (id, nextTab) => {
    setFocusId(id);
    if (nextTab) setTab(nextTab);
  };

  if (err) return <div className="aw-root"><div className="aw-card aw-empty" data-testid="aw-error">{err}</div></div>;
  if (!wf) return <div className="aw-root"><div className="aw-card aw-empty">Loading asset workflow…</div></div>;
  const s = wf.summary || {};
  const approvedN = Object.values(reviews).filter((r) => r.status === 'approved').length;
  const exportOpts = { reviews, audit, freeze, selection: selected.length ? selected : null };
  // Exports carry the deal-wide audit trail, not just the focused well's.
  const runExport = async (fn) => {
    let dealAudit = audit;
    try {
      dealAudit = await fetchDealAudit(dealId);
    } catch {
      /* fall back to what is loaded */
    }
    return fn(wf, { ...exportOpts, audit: dealAudit });
  };
  const vol = eurUnit(wf);

  return (
    <div className="aw-root" data-testid="asset-workflow">
      <div className="aw-header">
        <div className="aw-hazard">CONFIDENTIAL · {wf.meta?.confidentiality || 'CA restricted'} · not for public demo</div>
        <div className="aw-head-row">
          <div>
            <div className="aw-title">{wf.meta?.label || wf.dealId} <span>· Core asset workflow</span></div>
            <div className="aw-sub">Seller database and well records → canonical asset model → multi-well DCA → review → type curves → export</div>
          </div>
          <div className="aw-kpis">
            <div><label>Wells</label><b>{s.wells}</b></div>
            <div><label>Fitted</label><b>{s.fitted}/{s.withHistory}</b></div>
            <div><label>D2D remaining</label><b>{mmcf(s.remainingTotal)} {vol}</b></div>
            <div><label>Seller remaining</label><b>{s.ariesRemainingTotal ? `${mmcf(s.ariesRemainingTotal)} ${vol}` : '—'}</b></div>
            <div><label>Approved</label><b className="aw-amber">{approvedN}</b></div>
            <div><label>Selection</label><b>{selected.length || 'all'}</b></div>
          </div>
          <div className="aw-head-actions">
            <button type="button" className="aw-btn-outline" onClick={() => runExport(downloadWorkflowXlsx)} data-testid="export-xlsx">Excel (.xlsx)</button>
            <button type="button" className="aw-btn-outline" onClick={() => runExport(downloadWorkflowDocx)} data-testid="export-docx">Memo (.docx)</button>
          </div>
        </div>
        <nav className="aw-tabs">
          {AW_TABS.map((t) => (
            <button key={t.key} type="button" className={tab === t.key ? 'active' : ''} onClick={() => setTab(t.key)} data-testid={`awtab-${t.key}`}>{t.label}</button>
          ))}
        </nav>
      </div>
      {toast && <div className="aw-toast" role="status" onAnimationEnd={() => setToast(null)}>{toast}</div>}
      <div className="aw-body">
        {tab === 'map' && (
          <div className="aw-grid-map">
            <WellMapV2 wells={wells} selected={selected} focusId={focusId} onFocus={(id) => setFocusId(id)} onSelect={setSelected} reviews={reviews} product={wf.product} />
            <div className="aw-card" data-testid="map-decline-panel">
              {focus ? (
                <>
                  <div className="aw-toolbar">
                    <span className="aw-eyebrow">{focus.name}</span>
                    <Pill status={reviews[focus.assetId]?.status} provisional={reviews[focus.assetId]?.provisional} />
                    <button type="button" className="aw-btn" onClick={() => setTab('decline')} data-testid="open-workbench">Open in workbench →</button>
                  </div>
                  {focus.history?.length ? (
                    <DeclineChart history={focus.history} series={wellSeries(focus)} height={240} unit={wf.units?.rate} testId="map-chart" {...historyCaption(focus)} />
                  ) : (
                    <div className="aw-empty">No production history ({focus.status || 'unmodeled'}).</div>
                  )}
                  <div className="aw-small aw-mono">EUR {mmcf(focus.fit?.eur)} {vol} · remaining {mmcf(focus.fit?.remaining)} · seller {mmcf(focus.aries?.remaining)} · grade {focus.fit?.quality || '—'}</div>
                </>
              ) : (
                <div className="aw-empty">Click a well.</div>
              )}
              {selected.length > 0 && (
                <div className="aw-selection" data-testid="selection-summary">
                  <b>{selected.length} wells selected</b> · EUR {mmcf(wells.filter((w) => selected.includes(w.assetId)).reduce((a, w) => a + (w.fit?.eur || 0), 0))} {vol} ·{' '}
                  <button type="button" className="aw-link" onClick={() => setTab('typecurve')} data-testid="selection-typecurve">type curve →</button>{' '}
                  <button type="button" className="aw-link" onClick={() => setTab('decline')}>review →</button>{' '}
                  <button
                    type="button"
                    className="aw-link"
                    data-testid="selection-to-abs"
                    onClick={() => {
                      savePool(window.sessionStorage, dealId, selected);
                      window.location.hash = '#abs/link';
                    }}
                  >
                    send to ABS →
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {tab === 'decline' && (
          <div className="aw-grid-wb">
            <WellList wells={listWells} focusId={focusId} onFocus={setFocusId} reviews={reviews} selected={selected} />
            <Workbench wf={wf} well={focus} reviews={reviews} audit={audit} canApprove={canApprove} onReview={onReview} busy={busy} />
          </div>
        )}
        {tab === 'typecurve' && <TypeCurves wf={wf} wells={wells} selected={selected} />}
        {tab === 'review' && <ReviewBoard wf={wf} reviews={reviews} freeze={freeze} onFreeze={onFreeze} busy={busy} onFocus={focusWell} selected={selected} />}
        {tab === 'kb' && <KnowledgeBase wf={wf} reviews={reviews} onFocus={focusWell} />}
        {tab === 'dataroom' && <DataRoom wf={wf} />}
      </div>
      <div className="aw-footer">Model output (D2D fits) · Fact (seller forecast case, production history) · Judgment (overrides, approvals) — every number traces to Sources &amp; provenance.</div>
    </div>
  );
}
